import { useState, useEffect, useCallback, useMemo } from 'react'
import GenericTable from '../GenericTable'
import { d1Query, d1Insert, d1Update, d1Delete } from '../../services/d1Service'
import type { TeacherStaff, StatusMessage } from '../../types'

interface Props { eiin: string; onStatus: (msg: StatusMessage) => void }

const CATEGORY_META: Record<string, { label: string; bg: string; color: string }> = {
  teacher: { label: '👨‍🏫 Teacher', bg: '#dbeafe', color: '#1d4ed8' },
  staff_revenue: { label: '💼 Staff (Revenue)', bg: '#fef3c7', color: '#b45309' },
  staff_3rd_4th: { label: '🧹 Staff (3rd/4th)', bg: '#e0e7ff', color: '#4338ca' },
  library: { label: '📚 Library', bg: '#dcfce7', color: '#15803d' }
}

// Collect distinct, previously-entered values for a field so they show up
// as autocomplete suggestions the next time someone adds/edits a record.
function uniqueOptions(items: TeacherStaff[], key: keyof TeacherStaff): { value: string; label: string }[] {
  const seen = new Set<string>()
  items.forEach(item => {
    const v = String((item as any)[key] ?? '').trim()
    if (v) seen.add(v)
  })
  return Array.from(seen).sort((a, b) => a.localeCompare(b)).map(v => ({ value: v, label: v }))
}

export default function TeachersStaffTab({ eiin, onStatus }: Props) {
  const [items, setItems] = useState<TeacherStaff[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<Partial<TeacherStaff> | null>(null)
  const [search, setSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<string>('')

  const load = useCallback(async () => {
    setLoading(true)
    try { setItems(await d1Query<TeacherStaff>(`SELECT * FROM teachers_staff WHERE institution_eiin = ? ORDER BY sort_order ASC`, [eiin])) }
    catch (e: any) { onStatus({ type: 'error', text: e.message }) }
    finally { setLoading(false) }
  }, [eiin])

  useEffect(() => { load() }, [load])

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editing) return
    try {
      const rec = { ...editing, institution_eiin: eiin }
      if ((editing as any).id) await d1Update('teachers_staff', (editing as any).id, rec)
      else await d1Insert('teachers_staff', rec)
      onStatus({ type: 'success', text: 'Teacher/Staff saved!' })
      setEditing(null); load()
    } catch (e: any) { onStatus({ type: 'error', text: e.message }) }
  }

  // When picking an already-used Department / Designation from the dropdown,
  // auto-fill the paired code/BN/EN fields if they are still empty.
  const handleFieldChange = (key: string, val: any) => {
    setEditing(prev => {
      if (!prev) return prev
      const next: Partial<TeacherStaff> = { ...prev, [key]: val }
      if (key === 'department_bn') {
        const match = items.find(it => it.department_bn === val)
        if (match) {
          if (!next.department_code) next.department_code = match.department_code
          if (!next.department_en) next.department_en = match.department_en
        }
      } else if (key === 'department_code') {
        const match = items.find(it => it.department_code === val)
        if (match) {
          if (!next.department_bn) next.department_bn = match.department_bn
          if (!next.department_en) next.department_en = match.department_en
        }
      } else if (key === 'designation_bn') {
        const match = items.find(it => it.designation_bn === val)
        if (match && !next.designation_en) next.designation_en = match.designation_en
      }
      return next
    })
  }

  const departmentCodeOptions = useMemo(() => uniqueOptions(items, 'department_code'), [items])
  const departmentBnOptions = useMemo(() => uniqueOptions(items, 'department_bn'), [items])
  const departmentEnOptions = useMemo(() => uniqueOptions(items, 'department_en'), [items])
  const designationBnOptions = useMemo(() => uniqueOptions(items, 'designation_bn'), [items])
  const designationEnOptions = useMemo(() => uniqueOptions(items, 'designation_en'), [items])

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    items.forEach(it => { counts[it.category] = (counts[it.category] ?? 0) + 1 })
    return counts
  }, [items])

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase()
    return items.filter(it => {
      if (categoryFilter && it.category !== categoryFilter) return false
      if (!q) return true
      return [it.name_bn, it.name_en, it.department_bn, it.department_en, it.designation_bn, it.designation_en]
        .some(v => String(v ?? '').toLowerCase().includes(q))
    })
  }, [items, search, categoryFilter])

  // Sort by Department first so everyone from the same department is
  // clustered together in the table, then by sort_order within it.
  const groupedItems = useMemo(() => {
    return [...filteredItems].sort((a, b) => {
      const deptCompare = (a.department_bn ?? '').localeCompare(b.department_bn ?? '')
      if (deptCompare !== 0) return deptCompare
      return (a.sort_order ?? 0) - (b.sort_order ?? 0)
    })
  }, [filteredItems])

  return (
    <div>
      {/* Search & Category Filter */}
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '16px' }}>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="🔍 নাম, বিভাগ বা পদবি দিয়ে খুঁজুন..."
          style={{
            flex: '1 1 240px', padding: '9px 12px', borderRadius: '8px',
            border: '1.5px solid #fed7aa', fontSize: '13px', outline: 'none', boxSizing: 'border-box'
          }}
        />
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => setCategoryFilter('')}
            style={{
              padding: '6px 12px', borderRadius: '999px', cursor: 'pointer', fontSize: '12px', fontWeight: 700,
              border: `1.5px solid ${categoryFilter === '' ? '#ea580c' : '#fed7aa'}`,
              background: categoryFilter === '' ? '#ea580c' : '#fff',
              color: categoryFilter === '' ? '#fff' : '#9a3412'
            }}
          >All ({items.length})</button>
          {Object.entries(CATEGORY_META).map(([value, meta]) => (
            <button
              key={value}
              type="button"
              onClick={() => setCategoryFilter(value)}
              style={{
                padding: '6px 12px', borderRadius: '999px', cursor: 'pointer', fontSize: '12px', fontWeight: 700,
                border: `1.5px solid ${categoryFilter === value ? '#ea580c' : '#fed7aa'}`,
                background: categoryFilter === value ? '#ea580c' : '#fff',
                color: categoryFilter === value ? '#fff' : '#9a3412'
              }}
            >{meta.label} ({categoryCounts[value] ?? 0})</button>
          ))}
        </div>
      </div>

      <GenericTable
        title="Teachers & Staff" icon="👩‍🏫"
        items={groupedItems} loading={loading} editingItem={editing}
        groupBy={{ key: 'department_bn', label: (v) => `🏢 ${v || 'বিভাগ নির্ধারিত নেই'}` }}
        columns={[
          {
            key: 'photo_url', label: '', width: '52px',
            render: (v) => v ? (
              <img src={v} alt="" style={{ width: '34px', height: '34px', borderRadius: '50%', objectFit: 'cover', border: '1px solid #fed7aa', display: 'block' }} />
            ) : (
              <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: '#fff7ed', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px' }}>👤</div>
            )
          },
          { key: 'name_bn', label: 'Name (BN)', width: '160px' },
          { key: 'designation_bn', label: 'Designation', width: '150px' },
          { key: 'department_bn', label: 'Department', width: '130px' },
          {
            key: 'category', label: 'Category', width: '150px',
            render: (v) => {
              const meta = CATEGORY_META[v] ?? { label: v, bg: '#f1f5f9', color: '#475569' }
              return (
                <span style={{ padding: '3px 10px', borderRadius: '999px', fontSize: '11px', fontWeight: 700, background: meta.bg, color: meta.color, whiteSpace: 'nowrap' }}>
                  {meta.label}
                </span>
              )
            }
          },
          { key: 'phone', label: 'Phone' }
        ]}
        formFields={[
          { key: 'name_bn', label: 'Name (BN)', required: true },
          { key: 'name_en', label: 'Name (EN)', required: true },
          { key: 'designation_bn', label: 'Designation (BN)', type: 'combo', options: designationBnOptions, required: true, placeholder: 'যেমন: প্রভাষক', hint: 'আগে যোগ করা পদবি দেখতে টাইপ করুন বা তালিকা থেকে বেছে নিন' },
          { key: 'designation_en', label: 'Designation (EN)', type: 'combo', options: designationEnOptions, required: true },
          { key: 'department_code', label: 'Department Code', type: 'combo', options: departmentCodeOptions, required: true },
          { key: 'department_bn', label: 'Department (BN)', type: 'combo', options: departmentBnOptions, required: true, hint: 'পূর্বের বিভাগ নির্বাচন করলে কোড ও ইংরেজি নাম নিজে থেকেই বসে যাবে' },
          { key: 'department_en', label: 'Department (EN)', type: 'combo', options: departmentEnOptions, required: true },
          { key: 'category', label: 'Category', type: 'select', options: [
            { value: 'teacher', label: 'Teacher' },
            { value: 'staff_revenue', label: 'Staff (Revenue)' },
            { value: 'staff_3rd_4th', label: 'Staff (3rd/4th Class)' },
            { value: 'library', label: 'Library' }
          ]},
          { key: 'email', label: 'Email' },
          { key: 'phone', label: 'Phone' },
          { key: 'photo_url', label: 'Photo', type: 'image', folder: 'teachers' },
          { key: 'sort_order', label: 'Sort Order', type: 'number' }
        ]}
        onAdd={() => setEditing({ name_bn: '', name_en: '', designation_bn: '', designation_en: '', department_code: '', department_bn: '', department_en: '', category: 'teacher', sort_order: 1 })}
        onEdit={(item) => setEditing({ ...item })}
        onDelete={async (id) => { try { await d1Delete('teachers_staff', id); onStatus({ type: 'success', text: 'Deleted!' }); load() } catch (e: any) { onStatus({ type: 'error', text: e.message }) } }}
        onSave={handleSave}
        onCancel={() => setEditing(null)}
        onFieldChange={handleFieldChange}
      />
    </div>
  )
}
